import React from "react";
import { Skeleton } from "../../../ui/skeleton";
import { cn } from "../../../lib/utils";
import { count, monthLong, pct } from "../utils/format";
import { useMoney } from "../utils/currency";

/**
 * KPI strip (§4.1) — six equal cards, one large figure each with a sentence-case
 * label and a single muted sub-line. No sparklines in v1.
 *
 * Every figure here is derived from pnl.totals / pnl.monthly. Nothing invented:
 * no engagement score, no LTV, no churn probability (§8).
 */

/** "top 5: 5,000 / 3,000 / ..." — but never "top 1" when there is only one. */
function depositsSubLine(deposits, amount) {
  if (!deposits || !deposits.length) return "no deposits yet";
  const list = deposits.map(amount).join(" / ");
  return deposits.length === 1 ? "only deposit on record" : `top ${deposits.length}: ${list}`;
}

function KpiCard({ label, value, sub, tone }) {
  // Six cards share one row, so no single figure may decide the row's shape:
  // long values step down a size and are allowed to break rather than
  // overflow their card, and the figure line is fixed-height so every card
  // keeps the same silhouette whether its value is "14.2%" or "USDT -1.8M".
  const long = String(value ?? "").length > 11;
  return (
    <div className="min-w-0 rounded-lg border bg-card px-4 py-3.5">
      <div className="truncate text-xs leading-none text-muted-foreground">{label}</div>
      <div
        className={cn(
          "mt-2 flex h-7 items-center font-bold leading-none tracking-tight tabular-nums",
          long ? "text-lg" : "text-2xl",
          tone === "ggr" ? "text-chart-positive" : "text-foreground"
        )}
      >
        <span className="min-w-0 truncate" title={String(value ?? "")}>
          {value}
        </span>
      </div>
      <div className="mt-1.5 truncate text-xs leading-tight text-muted-foreground" title={sub}>
        {sub}
      </div>
    </div>
  );
}

export function KpiStrip({ kpis, loading }) {
  // Hook before the early returns so it runs on every render.
  const { money, moneyExact, amount } = useMoney();

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="rounded-lg border bg-card px-4 py-3.5">
            <Skeleton className="h-2.5 w-20" />
            <Skeleton className="mt-3 h-7 w-24" />
            <Skeleton className="mt-2.5 h-2.5 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (!kpis) return null;

  const cards = [
    {
      label: "Lifetime turnover",
      value: money(kpis.turnover),
      sub: `${count(kpis.bets)} bets`,
    },
    {
      label: "Lifetime GGR",
      value: money(kpis.ggr),
      // Player-perspective net is secondary text only (§2.1).
      sub: `player net −${money(Math.abs(kpis.playerNet))}`,
      tone: "ggr",
    },
    {
      label: "Hold",
      value: pct(kpis.hold, 1),
      sub: `Casino ${pct(kpis.casinoHold)}, Sports ${pct(kpis.sportsHold)}`,
    },
    {
      label: "Avg stake",
      // Exact, to the currency's own precision — rounding to a whole unit would
      // turn a 0.0004 BTC average into "0".
      value: moneyExact(kpis.avgStake),
      sub: "across all bets",
    },
    {
      label: "GGR this month",
      value: kpis.monthGgr === null ? "—" : money(kpis.monthGgr),
      sub: kpis.monthKey ? monthLong(kpis.monthKey) : "no activity yet",
      tone: "ggr",
    },
    {
      label: "Largest deposit",
      value: kpis.largestDeposit === null ? "—" : moneyExact(kpis.largestDeposit),
      sub: depositsSubLine(kpis.deposits, amount),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {cards.map((card) => (
        <KpiCard key={card.label} {...card} />
      ))}
    </div>
  );
}
